/**
 * LA MODALE DE DÉTAIL D'UN TITRE — sa fiche et sa VIE (émission, consommation, extinction).
 *
 * ⚠️ LECTURE SEULE, assumée et DITE à l'écran : un titre naît et meurt dans une pièce fiscale, en
 *    caisse. Un back-office qui en créditerait un fabriquerait de la valeur hors piste d'audit.
 * ⚠️ LA « VIE » NE S'INVENTE PAS. Aucun journal de titres n'existe en base : le serveur rend les
 *    FAITS qu'elle tient (dates d'émission et de consommation, pièces, reliquat). Une chronologie
 *    plus riche demanderait une table, pas une interpolation d'écran (§23.0).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { Badge, Icon, Trans } from '@orgazone/ui-web';
import { api, type TitreDetail } from '../api';
import { ETAT_TITRE } from './clients-kit';
import { dateCourte, euros, pourcent, typeUi } from './titres';
import { t } from '../i18n';

const MONO = 'var(--font-mono)';
const dateHeure = (ms: number): string =>
  new Date(ms).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** Une paire libellé/valeur de l'identité du titre. */
function Champ({ l, children }: { l: string; children: ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <span style={{ display: 'block', fontSize: '10px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{l}</span>
      <span style={{ display: 'block', fontSize: '13px', color: 'var(--ink-800)', marginTop: 2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>{children}</span>
    </div>
  );
}

export function DetailTitre({ code, onClose, onOuvrirVente, onOuvrirClient }: {
  code: string;
  onClose: () => void;
  onOuvrirVente: (pieceId: string) => void;
  onOuvrirClient: (clientId: string) => void;
}) {
  const [d, setD] = useState<TitreDetail | null>(null);
  const [failure, setFailure] = useState<string | null>(null);

  useEffect(() => {
    // ⚠️ CAPTURE (`true`) : sans elle, Échap fermerait aussi l'écran derrière.
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  useEffect(() => {
    let vivant = true;
    setD(null); setFailure(null);
    api.voucher(code)
      .then((r) => { if (vivant) setD(r); })
      .catch((e: unknown) => { if (vivant) setFailure(e instanceof Error ? e.message : String(e)); });
    return () => { vivant = false; };
  }, [code]);

  const r = d?.row;
  const ui = r ? typeUi(r.type) : null;

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 950, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }} role="dialog" aria-modal="true" aria-label={t('Titre {code}', { code })}>
      <div onClick={onClose} style={{ position: 'absolute', inset: 0, background: 'rgba(12,14,16,.42)' }} aria-hidden />
      <div style={{ position: 'relative', width: 'min(94vw, 620px)', maxHeight: '86vh', display: 'flex', flexDirection: 'column', background: 'var(--paper)', borderRadius: 'var(--r-lg)', boxShadow: '0 28px 70px -10px rgba(0,0,0,.45)', overflow: 'hidden' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--ink-200)' }}>
          <span style={{ width: 44, height: 44, borderRadius: 'var(--r-ctrl)', background: ui?.bg ?? 'var(--ink-100)', color: ui?.fg ?? 'var(--ink-500)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon name={ui?.icon ?? 'gift'} size={20} stroke={1.9} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={{ display: 'block', fontFamily: MONO, fontSize: '14px', fontWeight: 700, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis' }}>{code}</span>
            <span style={{ display: 'block', fontSize: '12px', color: 'var(--ink-500)' }}>
              {ui && r ? `${ui.l} · ${r.kind === 'titre' ? t('titre de paiement') : t('remise')}` : t('Chargement…')}
            </span>
          </span>
          {r && <Badge tone={ETAT_TITRE[r.state].tone} size="md">{ETAT_TITRE[r.state].l}</Badge>}
          <button type="button" onClick={onClose} aria-label={t('Fermer')}
            style={{ width: 32, height: 32, border: 'none', borderRadius: 'var(--r-ctrl)', background: 'var(--ink-100)', color: 'var(--ink-600)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>

        <div style={{ overflowY: 'auto', flex: 1 }}>
          {failure && (
            // Le message RÉEL de l'échec — jamais un « indisponible » qui cacherait la cause.
            <div style={{ margin: 16, padding: '12px 14px', border: '1px solid var(--rose-fg)', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', fontSize: '13px', color: 'var(--rose-fg)' }}>
              {t('Le titre n’a pas pu être lu : {failure}', { failure })}
            </div>
          )}
          {!d && !failure && <p style={{ margin: 0, padding: '38px 18px', textAlign: 'center', color: 'var(--ink-500)', fontSize: '13px' }}>{t('Lecture du titre…')}</p>}

          {d && r && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(148px, 1fr))', gap: 14, padding: '16px 18px' }}>
                <Champ l={t('Montant')}>
                  <strong style={{ fontSize: '15px', color: 'var(--ink-900)' }}>{r.discountPctBp != null ? pourcent(r.discountPctBp) : euros(r.amountCents)}</strong>
                </Champ>
                <Champ l={t('Reliquat')}>
                  {r.discountPctBp != null
                    ? <span style={{ color: 'var(--ink-400)' }}>{t('sans objet (remise)')}</span>
                    : <strong style={{ fontSize: '15px', color: r.remainingCents > 0 ? 'var(--teal-700)' : 'var(--ink-400)' }}>{euros(r.remainingCents)}</strong>}
                </Champ>
                <Champ l={t('Client')}>
                  {r.clientId
                    ? (
                      <button type="button" onClick={() => onOuvrirClient(r.clientId as string)}
                        style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', color: 'var(--teal-700)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                        {r.clientName ?? t('Fiche client')}
                      </button>
                    )
                    : <span style={{ color: 'var(--ink-400)' }}>{t('Porteur libre (au porteur)')}</span>}
                </Champ>
                <Champ l={t('Émis le')}>{dateCourte(r.issuedAt)}</Champ>
                <Champ l={t('Valide à partir du')}>{r.validFrom == null ? <span style={{ color: 'var(--ink-400)' }}>{t('immédiatement')}</span> : dateCourte(r.validFrom)}</Champ>
                <Champ l={t('Valide jusqu’au')}>
                  {r.expiresAt == null
                    ? <span style={{ color: 'var(--ink-400)' }}>{t('sans limite')}</span>
                    : <>
                      {dateCourte(r.expiresAt)}
                      {/* ⚠️ La règle qui l'a produite se LIT — sinon une date sortie de nulle part
                          n'est ni vérifiable ni discutable au comptoir. */}
                      {d.validityRule && (
                        <span style={{ display: 'block', fontSize: '11px', color: 'var(--ink-500)', marginTop: 1 }}>
                          {d.validityRule}
                        </span>
                      )}
                    </>}
                </Champ>
              </div>

              {/* ⚠️ « Sans limite » tout court laisserait croire à une décision : on dit LAQUELLE.
                  Et quand l'échéance a été CALCULÉE à la reprise, on le dit aussi — elle n'a jamais
                  figuré sur le papier du client, et un vendeur doit pouvoir l'expliquer (§23.0). */}
              {d.expiryNote && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', margin: '0 18px 14px', padding: '9px 11px', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', background: 'var(--ink-100)', fontSize: '11.5px', lineHeight: 1.5, color: 'var(--ink-600)' }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 2 }} aria-hidden>
                    <circle cx="12" cy="12" r="9" /><path d="M12 7.5V12l3 2" />
                  </svg>
                  <span>
                    {d.expiryNote}
                    {/* La règle EN VIGUEUR reste utile ici : elle dit ce qui s'appliquerait aujourd'hui. */}
                    {d.validityRule && <> <Trans k='Règle actuelle du magasin pour ce type : <1>{validityRule}</1>.' vars={{ validityRule: d.validityRule }} parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></>}
                  </span>
                </div>
              )}

              {(r.legacy || r.passePartout || d.parentCode || d.childCode) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, padding: '0 18px 14px' }}>
                  {r.legacy && <Badge tone="neutral" size="sm" radius="pill">{t('Repris de l’ancien logiciel')}</Badge>}
                  {r.passePartout && <Badge tone="warn" size="sm" radius="pill">{t('Passe-partout — injecté en caisse')}</Badge>}
                  {d.parentCode && <Badge tone="info" size="sm" radius="pill">{t('Reliquat de {parentCode}', { parentCode: d.parentCode })}</Badge>}
                  {d.childCode && <Badge tone="info" size="sm" radius="pill">{t('A engendré {childCode}', { childCode: d.childCode })}</Badge>}
                </div>
              )}

              <h3 style={{ margin: '4px 18px 8px', fontSize: '11px', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{t('Vie du titre')}</h3>
              <div style={{ padding: '0 18px 16px' }}>
                {d.events.map((e, i) => (
                  <div key={`${e.at}-${i}`} style={{ display: 'flex', alignItems: 'flex-start', gap: 11, padding: '9px 0', borderBottom: i < d.events.length - 1 ? '1px solid var(--ink-100)' : 'none' }}>
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--teal-400)', marginTop: 5, flexShrink: 0 }} aria-hidden />
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: 'var(--ink-800)' }}>{e.label}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 2, fontSize: '11.5px', color: 'var(--ink-500)', flexWrap: 'wrap' }}>
                        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{dateHeure(e.at)}</span>
                        {e.pieceId && (
                          // La pièce s'ouvre par IDENTIFIANT — même geste que depuis les mouvements de stock.
                          <button type="button" onClick={() => onOuvrirVente(e.pieceId as string)}
                            style={{ border: 'none', background: 'none', padding: 0, font: 'inherit', fontFamily: MONO, color: 'var(--teal-700)', fontWeight: 600, textDecoration: 'underline', textUnderlineOffset: 3, cursor: 'pointer' }}>
                            {e.pieceNumber || t('voir la pièce')}
                          </button>
                        )}
                      </span>
                    </span>
                    {e.deltaCents != null && (
                      <span style={{ fontSize: '13.5px', fontWeight: 700, fontVariantNumeric: 'tabular-nums', color: e.deltaCents >= 0 ? 'var(--teal-700)' : 'var(--rose-fg)', flexShrink: 0 }}>
                        {e.deltaCents >= 0 ? '+' : '−'}{euros(Math.abs(e.deltaCents))}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div style={{ display: 'flex', gap: 9, alignItems: 'flex-start', padding: '11px 18px', borderTop: '1px solid var(--ink-100)', background: 'var(--ink-100)', fontSize: '11.5px', lineHeight: 1.5, color: 'var(--ink-600)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: 1 }} aria-hidden>
            <circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" />
          </svg>
          <span>
            <Trans k='Un titre s’<1>émet</1> et se <2>consomme en caisse</2>, dans une pièce fiscale : le back-office le regarde, il ne l’écrit pas. La vie affichée ne montre que les faits que la base tient — dates, pièces et reliquat.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>, (x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></span>
        </div>
      </div>
    </div>
  );
}
